<template>
  <div class="w-full">
    <div v-if="groups.length > 0">
      <b-collapse v-for="(group, index) in groups" :key="'status_group_' + codeLayer + '_' + group.id"
          class="card mb-4"
          animation="slide"
          aria-id="contentIdForA11y3"
          :open="false">
          <template #trigger="props">
              <div
                  class="card-header"
                  role="button"
                  aria-controls="contentIdForA11y3"
                  :aria-expanded="props.open">
                  <div class="card-header-title flex flex-col items-start">
                    <p>{{ $t('group') + ': ' + group.name + ' - ' + group.weight + '%'}}</p>
                  </div>
                  <a class="card-header-icon">
                      <b-icon :icon="props.open ? 'chevron-down' : 'chevron-up'" pack="fas">
                      </b-icon>
                  </a>
              </div>
          </template>

          <div class="card-content p-2">
            <div class="mb-4 text-center">
              <b-button type="is-primary"
                size="is-small"
                @click="_handleOnClickEditGroup(group)">{{ $t('edit_group') }}</b-button>
              <b-button type="is-primary"
                size="is-small"
                @click="_handleOnClickDeleteGroup(group)">{{ $t('delete_group') }}</b-button>
              <b-button type="is-primary"
                size="is-small"
                @click="_handleOnClickAddStatus(group)">{{ $t('add_status') }}</b-button>
              <b-button v-if="ungroupedStates.length > 0"
                type="is-primary"
                size="is-small"
                @click="_handleOnClickLinkStatus(group)">{{ $t('link') }}</b-button>
            </div>

            <statuses :ref="'refStatusesGroups_' + codeLayer + '_' + group.id"
              :statuses="group.statuses"
              :layer-id="layerId"
              :code-layer="codeLayer"
              :group-id="group.id"
              :show-btn-add-status="false"
              :weighting-criteria="weightingCriteria"
              :visibility="visibility"
            />
          </div>

      </b-collapse>
    </div>
    <div v-else class="flex flex-row bg-grey-light border hover:border-blue rounded">
      <p class="text-center w-full">No tiene grupo definido</p>
    </div>

    <div class="border-t mt-4 border-b border-gray-100 text-center">
        <b-button type="is-primary"
          size="is-small"
          @click="_handleOnClickAddGroup">{{ $t('add_group') }}</b-button>
    </div>

    <group-status-modal :ref="'refGroupStatusModal'"
      @epc-group-status-add="_handleAddGroup"
    />

    <group-status-link-modal :ref="'refGroupStatusLinkModal'"
      @epc-group-status-link="_handleLinkStatusGroup"
    />

  </div>
</template>

<script>

import GroupStatusModal from './group-status-modal.vue';
import GroupStatusLinkModal from './group-status-link-modal.vue';
import Statuses from '../statuses/statuses.vue';

export default {
  name: 'LayerGroupStatuses',

  components: {
    GroupStatusLinkModal,
    GroupStatusModal,
    Statuses
  },

  // Inyecciones del manager (LayerStatusesManager). Cada una es async (Promise).
  // Si el manager no está montado arriba (uso aislado), reciben null y los
  // handlers no se rompen (silent no-op + warning si llega).
  inject: {
    AddGroup:          { from: 'AddGroup',          default: null },
    UpdateGroup:       { from: 'UpdateGroup',       default: null },
    DeleteGroup:       { from: 'DeleteGroup',       default: null },
    LinkStatusToGroup: { from: 'LinkStatusToGroup', default: null },
  },

  props: {
    groups: {
      type: Array,
      required: true
    },
    ungroupedStates: {
      type: Array,
      default: () => []
    },
    codeLayer: {
      type: String,
      required: true
    },
    layerId: {
      type: Number,
      required: true
    },
    weightingCriteria: {
      type: String,
      default: null
    },
    visibility: {
      type: String,
      default: 'visible_default'
    }
  },

  data() {
    return {};
  },

  methods: {
    _handleOnClickLinkStatus(group){
      this.$refs.refGroupStatusLinkModal.show({
        data: this.ungroupedStates,
        weightingCriteria: this.weightingCriteria,
        group,
        layerId: this.layerId
      });
    },

    _handleOnClickAddStatus(group) {
      const ref = this.$refs['refStatusesGroups_' + this.codeLayer + '_' + group.id];
      if (Array.isArray(ref)) {
        ref[0]?.openAddModal?.(); // si es array, accede al primero
      } else {
        ref?.openAddModal?.(); // si no, accede directamente
      }
    },

    _handleOnClickDeleteGroup(group) {
      this._handleDeleteElement(group);
    },

    _handleOnClickEditGroup(group) {
      this.$refs.refGroupStatusModal.show(group, this.layerId);
    },

    _handleOnClickAddGroup() {
      this.$refs.refGroupStatusModal.show({
        id: null,
        parent_id: null,
        name: '',
        weight: null,
        type: 'group',
      }, this.layerId);
    },

    // Método público para que el padre pueda disparar el modal sin pasar por click
    addGroup() {
      this._handleOnClickAddGroup();
    },

    /**
     * El modal de crear/editar grupo (group-status-modal) emite con un
     * payload puro. Distinguimos create vs edit por el id:
     *   - id null → AddGroup
     *   - id > 0  → UpdateGroup
     * (No vamos a recibir id negativo acá; los temporales vivirán solo
     * dentro del manager mientras espera el echo del backend.)
     */
    async _handleAddGroup({ payload }) {
      if (!this.AddGroup || !this.UpdateGroup) {
        console.warn('[group-statuses] inject del manager no disponible');
        return;
      }
      const { id, ...rest } = payload;
      try {
        if (id && id > 0) {
          await this.UpdateGroup(id, rest);
        } else {
          await this.AddGroup(rest);
        }
        this.$toast.open({
          message: this.$t('status_change_success'),
          type: 'is-success',
          position: 'is-top-right',
        });
      } catch (err) {
        this.$toast.open({
          message: err.error_msg || this.$t('status_change_fail'),
          type: 'is-danger',
          position: 'is-top-right',
        });
      }
    },

    async _handleDeleteElement(group) {
      if (!this.DeleteGroup) {
        console.warn('[group-statuses] inject DeleteGroup no disponible');
        return;
      }
      try {
        await this.DeleteGroup(group.id);
        this.$toast.open({
          message: this.$t('status_deleted_successfully'),
          type: 'is-success',
          position: 'is-top-right',
        });
      } catch (err) {
        this.$toast.open({
          message: err.error_msg || this.$t('error_deleting_status'),
          type: 'is-danger',
          position: 'is-top-right',
        });
      }
    },

    /**
     * El modal de link (group-status-link-modal) emite { status, groupId }.
     * status es el ungrouped que el usuario seleccionó, groupId es el grupo
     * de destino. El manager hace el POST link + actualiza parent_id.
     */
    async _handleLinkStatusGroup({ status, groupId }) {
      if (!this.LinkStatusToGroup) {
        console.warn('[group-statuses] inject LinkStatusToGroup no disponible');
        return;
      }

      try {
        await this.LinkStatusToGroup(status.id, groupId);
        this.$toast.open({
          message: this.$t('status_change_success'),
          type: 'is-success',
          position: 'is-top-right',
        });
      } catch (err) {
        this.$toast.open({
          message: err.error_msg || this.$t('status_change_fail'),
          type: 'is-danger',
          position: 'is-top-right',
        });
      }
    },
  }
};
</script>
